import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Handle, Position, NodeResizer, useReactFlow } from 'reactflow';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
import { setTabPanel } from '../../../../redux/reducers/global/editor/editorSlice';
import { EDITOR_TAB_PANEL_INSPECTOR, VERTICAL_ORIENTATION } from '../../../../utilities/constants/editorConstants';
import { selectSingleNode, setCurrentNodeId, setNodesActionCreator, toggleNodeSelection, updateNodeAttributes } from '../../../../redux/reducers/global/node/nodeSlice';
import { setEdgesActionCreator } from '../../../../redux/reducers/global/edge/edgeSlice';
import { setScene } from '../../../../redux/reducers/global/inspector/inspectorSlice';
import { BUS_HANDLE_GAP_PX, BUS_THICKNESS_PX, MIN_BUS_HANDLES, parseBusHandleIndex } from '../../../../utilities/busLayout';
import { setResizingBusNodeId } from '../../../../redux/reducers/global/view/viewSlice';

const EMPTY_ARRAY = [];

const cloneNodeForStore = (node) => {
  if (!node || typeof node !== 'object') return node;
  const clone = JSON.parse(JSON.stringify(node));
  delete clone.selected;
  delete clone.dragging;
  delete clone.positionAbsolute;
  delete clone.style;
  return clone;
};

const cloneNodesForStore = (nodes) => (
  Array.isArray(nodes) ? nodes.map(cloneNodeForStore) : []
);

const cloneEdgeForStore = (edge) => {
  if (!edge || typeof edge !== 'object') return edge;
  const clone = JSON.parse(JSON.stringify(edge));
  delete clone.selected;
  delete clone.dragging;
  return clone;
};

const cloneEdgesForStore = (edges) => (
  Array.isArray(edges) ? edges.map(cloneEdgeForStore) : []
);

const toHandleClassToken = (value, fallback = 'default') => {
  const text = typeof value === 'string' && value.length > 0 ? value : fallback;
  return text.replace(/[^a-zA-Z0-9_-]/g, '_');
};

const stripDiacritics = (value = '') =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeText = (value) =>
  typeof value === 'string' ? stripDiacritics(value).trim().toLowerCase() : '';

const UNIT_SUFFIX_BY_KEY = {
  ratedVoltage: 'V',
  ratedCurrent: 'A',
  shortCircuitCurrent: 'A',
};

const CURRENT_UNIT_KEYS = new Set(['ratedCurrent', 'shortCircuitCurrent']);
const VOLTAGE_UNIT_KEYS = new Set(['ratedVoltage']);

const resolveUnitSuffix = (fieldKey, fieldLabel) => {
  if (fieldKey && UNIT_SUFFIX_BY_KEY[fieldKey]) {
    return UNIT_SUFFIX_BY_KEY[fieldKey];
  }
  const normalized = normalizeText(fieldLabel);
  if (!normalized) return null;
  if (normalized.includes('voltaje nominal')) return 'V';
  if (normalized.includes('corriente nominal')) return 'A';
  if (normalized.includes('corto circuito') || normalized.includes('corriente de corto')) return 'A';
  return null;
};

const normalizeTintHex = (value) => {
  if (typeof value !== 'string') return null;
  let hex = value.trim();
  if (!hex) return null;
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }
  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    hex = `#${hex
      .slice(1)
      .split('')
      .map((ch) => ch + ch)
      .join('')}`;
  }
  if (!/^#([0-9a-fA-F]{6})$/.test(hex)) return null;
  return hex.toLowerCase();
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = normalizeTintHex(hex);
  if (!normalized) return null;
  const numeric = parseInt(normalized.slice(1), 16);
  const r = (numeric >> 16) & 255;
  const g = (numeric >> 8) & 255;
  const b = numeric & 255;
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

const BusNode = ({ id, data, selected }) => {
  const dispatch = useDispatch();
  const busRef = useRef(null);
  const gridGap = BUS_HANDLE_GAP_PX;
  const minSegments = Math.max(1, MIN_BUS_HANDLES - 1);
  const minLength = gridGap * minSegments;
  const snapToGrid = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return 0;
      return Math.round(numeric / gridGap) * gridGap;
    },
    [gridGap],
  );
  const lengthAttributeValue = data?.attributes?.length?.value;
  const storedBusLength = data?.busLength;
  const [busLength, setBusLength] = useState(() => {
    const candidates = [storedBusLength, lengthAttributeValue];
    for (const candidate of candidates) {
      const numeric = Number(candidate);
      if (Number.isFinite(numeric) && numeric > 0) {
        return Math.max(minLength, numeric);
      }
    }
    return minLength;
  });
  const globalLabelFontPx = useSelector((state) => state.view.labelFontPx) || 38;
  const selectedNodeId = useSelector(state => state.node.selectedNodeId);
  const highlightKeys = useSelector((state) => state.view.highlightHandles) || [];
  const handlesHidden = useSelector((state) => !!state.view.handlesHidden);
  const connectionAssist = useSelector((state) => state.view.connectionAssist) || {};
  const labelPositionEditNodeId = useSelector((state) => state.view.labelPositionEditNodeId);
  const nodes = useSelector(state => state.node.nodes) || EMPTY_ARRAY;
  const edges = useSelector(state => state.edge.edges) || EMPTY_ARRAY;
  const reactFlowInstance = useReactFlow();
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);
  const setNodes = useCallback(
    (updater) => {
      const sourceNodes = reactFlowInstance?.getNodes?.() || nodesRef.current;
      const nextNodes = typeof updater === 'function' ? updater(sourceNodes) : updater;
      if (!Array.isArray(nextNodes)) return;
      try {
        reactFlowInstance?.setNodes?.(nextNodes);
      } catch {}
      dispatch(setNodesActionCreator({ nodes: cloneNodesForStore(nextNodes) }));
    },
    [dispatch, reactFlowInstance],
  );
  const isLabelPositionEditing = labelPositionEditNodeId === id;
  const [isLabelDragging, setIsLabelDragging] = useState(false);
  const [labelOffsetDraft, setLabelOffsetDraft] = useState(null);
  const labelDragStateRef = useRef(null);

  const visibleAttributes = useMemo(() => {
    const attrs = data?.attributes;
    if (!attrs) return [];
    return Object.entries(attrs)
      .filter(([, attr]) => {
        if (!attr || !attr.isVisible) return false;
        const raw = attr.value;
        if (raw === undefined || raw === null) return false;
        const text = typeof raw === 'string' ? raw.trim() : String(raw).trim();
        return text.length > 0;
      })
      .map(([key, attr]) => {
        const label = attr.label || key;
        const rawValue = attr?.value;
        const text = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();
        const explicitUnit = CURRENT_UNIT_KEYS.has(key)
          ? (['A', 'kA'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'A')
          : VOLTAGE_UNIT_KEYS.has(key)
            ? (['V', 'kV'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'V')
            : attr?.unitSuffix;
        const suffix = explicitUnit || resolveUnitSuffix(key, label);
        const value = suffix && text ? `${text} ${suffix}`.trim() : text;
        return {
          key,
          label,
          value,
        };
      });
  }, [data?.attributes]);
  const hideParameterLabelsDefault = useSelector((state) => !!state.view.hideParameterLabelsDefault);
  const attributeLabelSeparator = useSelector((state) => state.view.attributeLabelSeparator ?? ':');
  const hideLabels = typeof data?.hideAttributeLabels === 'boolean'
    ? data.hideAttributeLabels
    : hideParameterLabelsDefault;
  const baseLabelOffset = useMemo(() => {
    const raw = data?.labelOffset;
    const parsedX = Number(raw?.x);
    const parsedY = Number(raw?.y);
    return {
      x: Number.isFinite(parsedX) ? parsedX : 0,
      y: Number.isFinite(parsedY) ? parsedY : 0,
    };
  }, [data?.labelOffset]);
  const appliedLabelOffset = labelOffsetDraft ?? baseLabelOffset;
  const labelFontPx = useMemo(() => {
    const customSize = Number(data?.textSizePx);
    if (Number.isFinite(customSize) && customSize > 0) return customSize;
    return globalLabelFontPx;
  }, [data?.textSizePx, globalLabelFontPx]);
  const tintColor = useMemo(() => normalizeTintHex(data?.tintColor), [data?.tintColor]);
  const selectionAccent = tintColor || '#2cabef';
  const selectionGlow = useMemo(
    () => hexToRgba(selectionAccent, 0.25) || 'rgba(44, 171, 239, 0.25)',
    [selectionAccent],
  );
  const busColor = tintColor || '#111827';

  // selection and inspector sync handled in Diagram onSelectionChange

  // Determine rotation angle for bus
  const angleOffset = typeof data?.angle === 'number' ? data.angle : VERTICAL_ORIENTATION;
  // Thickness (minor axis + visual bar) is fixed
  const thicknessPx = BUS_THICKNESS_PX;
  // Keep handles only slightly separated from bar
  const handleOffset = Math.min(12, Math.max(6, Math.round(thicknessPx * 0.25)));
  const lateralHandleOffset = Math.round(thicknessPx / 2 + handleOffset);
  const horizontalBus = angleOffset === VERTICAL_ORIENTATION;
  const busMajorLength = Math.max(minLength, busLength || minLength);
  const busWidthPx = horizontalBus ? busMajorLength : thicknessPx;
  const busHeightPx = horizontalBus ? thicknessPx : busMajorLength;

  const getWrapperElement = useCallback(() => {
    if (!busRef.current) return null;
    return busRef.current.closest('.react-flow__node') || busRef.current.parentElement || null;
  }, []);
  const refreshNodeInternals = useCallback(() => {
    if (typeof window === 'undefined') {
      try { reactFlowInstance?.updateNodeInternals?.(id); } catch {}
      return;
    }
    window.requestAnimationFrame(() => {
      try { reactFlowInstance?.updateNodeInternals?.(id); } catch {}
    });
  }, [id, reactFlowInstance]);

  const snapLength = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return minLength;
      }
      const snapped = Math.round(numeric / gridGap) * gridGap;
      return Math.max(minLength, snapped);
    },
    [gridGap, minLength],
  );

  const normalizeLength = useCallback(
    (value) => {
      const snapped = snapLength(value);
      const segments = Math.max(minSegments, Math.round(snapped / gridGap));
      return segments * gridGap;
    },
    [snapLength, minSegments, gridGap],
  );

  const updateBusEdgesForResize = useCallback(({ previousAxisOrigin, nextAxisOrigin, previousSegmentCount, nextSegmentCount }) => {
    if (
      !Number.isFinite(previousAxisOrigin)
      || !Number.isFinite(nextAxisOrigin)
      || !Number.isFinite(previousSegmentCount)
      || !Number.isFinite(nextSegmentCount)
    ) {
      return;
    }

    const sourceEdges = typeof reactFlowInstance?.getEdges === 'function'
      ? reactFlowInstance.getEdges()
      : edgesRef.current;

    if (!Array.isArray(sourceEdges) || sourceEdges.length === 0) {
      return;
    }

    const maxPreviousHandleIndex = previousSegmentCount;
    const maxNextHandleIndex = nextSegmentCount;
    const nextAxisEnd = nextAxisOrigin + nextSegmentCount * gridGap;
    const EPS = 0.001;

    const remapIfStillPresent = (handleId, prefix) => {
      const previousHandleIndex = parseBusHandleIndex(handleId, maxPreviousHandleIndex + 1);
      const previousAxisPosition = previousAxisOrigin + previousHandleIndex * gridGap;
      if (previousAxisPosition < nextAxisOrigin - EPS || previousAxisPosition > nextAxisEnd + EPS) {
        return null;
      }
      const nextHandleIndex = Math.max(
        0,
        Math.min(maxNextHandleIndex, Math.round((previousAxisPosition - nextAxisOrigin) / gridGap)),
      );
      return `${prefix}-${nextHandleIndex}`;
    };

    let changed = false;
    const nextEdges = sourceEdges.reduce((acc, edge) => {
      if (!edge) {
        return acc;
      }
      let nextEdge = edge;
      if (edge.source === id) {
        const nextSourceHandle = remapIfStillPresent(edge.sourceHandle, 'handle');
        if (!nextSourceHandle) {
          changed = true;
          return acc;
        }
        if (nextSourceHandle !== edge.sourceHandle) {
          nextEdge = {
            ...nextEdge,
            sourceHandle: nextSourceHandle,
            data: {
              ...(nextEdge.data || {}),
              waypoints: [],
            },
          };
          changed = true;
        }
      }
      if (edge.target === id) {
        const nextTargetHandle = remapIfStillPresent(edge.targetHandle, 'target');
        if (!nextTargetHandle) {
          changed = true;
          return acc;
        }
        if (nextTargetHandle !== edge.targetHandle) {
          nextEdge = {
            ...nextEdge,
            targetHandle: nextTargetHandle,
            data: {
              ...(nextEdge.data || {}),
              waypoints: [],
            },
          };
          changed = true;
        }
      }
      acc.push(nextEdge);
      return acc;
    }, []);

    if (!changed) {
      return;
    }

    try {
      reactFlowInstance?.setEdges?.(nextEdges);
    } catch {}
    dispatch(setEdgesActionCreator({ edges: cloneEdgesForStore(nextEdges) }));
  }, [dispatch, gridGap, id, reactFlowInstance]);

  // Ensure edges re-anchor when angle changes
  useEffect(() => {
    refreshNodeInternals();
  }, [data?.angle, refreshNodeInternals]);

  // No wrapper mutation needed when the inner container sets explicit px size
  useEffect(() => {
    refreshNodeInternals();
  }, [angleOffset, busLength, refreshNodeInternals]);

  useEffect(() => {
    setBusLength((prev) => normalizeLength(prev));
  }, [normalizeLength]);

  useEffect(() => {
    const candidates = [storedBusLength, lengthAttributeValue];
    for (const candidate of candidates) {
      const numeric = Number(candidate);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        continue;
      }
      const normalized = normalizeLength(numeric);
      setBusLength((prev) => (prev === normalized ? prev : normalized));
      return;
    }
  }, [storedBusLength, lengthAttributeValue, normalizeLength]);

  useEffect(() => {
    const wrapper = getWrapperElement();
    if (!wrapper) return;
    wrapper.style.width = `${busWidthPx}px`;
    wrapper.style.height = `${busHeightPx}px`;
    refreshNodeInternals();
  }, [busWidthPx, busHeightPx, getWrapperElement, refreshNodeInternals]);

  const applyResize = useCallback(
    (params, emitAutoConnect = false) => {
      const wrapper = getWrapperElement();
      if (!wrapper) return;
      const isHorizontal = angleOffset === VERTICAL_ORIENTATION;
      const measurementFromParams = params
        ? isHorizontal
          ? params.width
          : params.height
        : null;
      const fallbackMeasurement = isHorizontal ? wrapper.offsetWidth : wrapper.offsetHeight;
      const measurement = Number.isFinite(measurementFromParams) ? measurementFromParams : fallbackMeasurement;
      const normalizedLength = params
        ? normalizeLength(measurement || gridGap)
        : Math.max(minLength, busLength || minLength);
      const nextWidth = isHorizontal ? normalizedLength : thicknessPx;
      const nextHeight = isHorizontal ? thicknessPx : normalizedLength;
      const widthChanged = Math.round(nextWidth) !== Math.round(wrapper.offsetWidth || 0);
      const heightChanged = Math.round(nextHeight) !== Math.round(wrapper.offsetHeight || 0);

      if (widthChanged || heightChanged) {
        wrapper.style.width = `${nextWidth}px`;
        wrapper.style.height = `${nextHeight}px`;
        refreshNodeInternals();
      }

      const lengthChanged = normalizedLength !== busLength;
      const previousNode = nodesRef.current.find((node) => node.id === id);
      const previousAxisOrigin = isHorizontal
        ? Number(previousNode?.position?.x)
        : Number(previousNode?.position?.y);
      const nextAxisOrigin = params
        ? isHorizontal
          ? (Number.isFinite(params.x) ? snapToGrid(params.x) : previousAxisOrigin)
          : (Number.isFinite(params.y) ? snapToGrid(params.y) : previousAxisOrigin)
        : previousAxisOrigin;
      const previousSegmentCount = Math.max(minSegments, Math.round((busLength || minLength) / gridGap));
      const nextSegmentCount = Math.max(minSegments, Math.round(normalizedLength / gridGap));

      if (params && emitAutoConnect && lengthChanged) {
        updateBusEdgesForResize({
          previousAxisOrigin,
          nextAxisOrigin,
          previousSegmentCount,
          nextSegmentCount,
        });
      }

      if (lengthChanged) {
        setBusLength(normalizedLength);
      }

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== id) return node;
          const widthDiff = Math.round(node.width || 0) !== Math.round(nextWidth);
          const heightDiff = Math.round(node.height || 0) !== Math.round(nextHeight);
          let positionChanged = false;
          let nextPosition = node.position;

          if (params) {
            const candidate = { ...(node.position || { x: 0, y: 0 }) };
            if (Number.isFinite(params.x)) {
              candidate.x = snapToGrid(params.x);
            }
            if (Number.isFinite(params.y)) {
              candidate.y = snapToGrid(params.y);
            }
            positionChanged = candidate.x !== (node.position?.x ?? 0) || candidate.y !== (node.position?.y ?? 0);
            nextPosition = candidate;
          }

          if (!widthDiff && !heightDiff && !positionChanged && !lengthChanged) {
            return node;
          }

          let nextData = node.data;
          if (lengthChanged) {
            const nextAttributes = {
              ...(node.data?.attributes || {}),
              length: {
                ...(node.data?.attributes?.length || { label: 'Longitud', isVisible: false }),
                value: normalizedLength,
                isVisible: false,
                visibilityUserModified: true,
                systemDefined: true,
              },
            };
            nextData = {
              ...(node.data || {}),
              busLength: normalizedLength,
              attributes: nextAttributes,
            };
          }

          return {
            ...node,
            width: widthDiff ? nextWidth : node.width,
            height: heightDiff ? nextHeight : node.height,
            position: positionChanged ? nextPosition : node.position,
            data: nextData,
          };
        }),
      );

    },
    [angleOffset, normalizeLength, thicknessPx, getWrapperElement, gridGap, busLength, minLength, minSegments, setNodes, id, snapToGrid, refreshNodeInternals, updateBusEdgesForResize],
  );

  const handleResize = useCallback(
    (event, params) => applyResize(params, false),
    [applyResize],
  );

  const handleResizeStart = useCallback(() => {
    dispatch(setResizingBusNodeId(id));
  }, [dispatch, id]);

  const handleResizeEnd = useCallback(
    (event, params) => {
      applyResize(params, true);
      dispatch(setResizingBusNodeId(null));
    },
    [applyResize, dispatch],
  );

  useEffect(() => () => {
    dispatch(setResizingBusNodeId(null));
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame(() => {
      applyResize(null, false);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [angleOffset, busLength, applyResize]);

  const segmentCount = useMemo(() => {
    if (!Number.isFinite(busLength) || busLength <= 0) return minSegments;
    return Math.max(minSegments, Math.round(busLength / gridGap));
  }, [busLength, gridGap, minSegments]);
  const handlePositions = useMemo(
    () => Array.from({ length: segmentCount + 1 }, (_, idx) => idx * gridGap),
    [segmentCount, gridGap],
  );
  const labelPositionConfig = useMemo(() => {
    if (!visibleAttributes.length) return null;
    if (angleOffset === VERTICAL_ORIENTATION) {
      const verticalOffset = Math.max(36, Math.round(thicknessPx * 0.5) + 78);
      return {
        style: {
          top: `-${verticalOffset}px`,
          left: '100%',
        },
        transform: 'translate(24px, 0)',
      };
    }
    return {
      style: {
        left: `${thicknessPx + 20}px`,
        top: '50%',
      },
      transform: 'translateY(-50%)',
    };
  }, [visibleAttributes.length, angleOffset, thicknessPx]);
  const labelStyle = useMemo(() => {
    if (!labelPositionConfig) return null;
    const style = { ...labelPositionConfig.style };
    const offsetX = Number(appliedLabelOffset?.x) || 0;
    const offsetY = Number(appliedLabelOffset?.y) || 0;
    const offsetTransform = `translate(${offsetX}px, ${offsetY}px)`;
    const baseTransform = labelPositionConfig.transform || '';
    style.transform = baseTransform ? `${baseTransform} ${offsetTransform}` : offsetTransform;
    style.pointerEvents = 'auto';
    style.cursor = isLabelPositionEditing ? (isLabelDragging ? 'grabbing' : 'grab') : 'default';
    style.touchAction = isLabelPositionEditing ? 'none' : 'auto';
    style.userSelect = 'none';
    style.WebkitUserSelect = 'none';
    style.fontSize = `${labelFontPx}px`;
    style.color = busColor;
    if (isLabelPositionEditing) {
      style.boxShadow = '0 0 0 2px rgba(44,171,239,0.45), 0 14px 28px rgba(15, 23, 42, 0.18)';
    }
    return style;
  }, [labelPositionConfig, appliedLabelOffset, isLabelPositionEditing, isLabelDragging, labelFontPx, busColor]);
  useEffect(() => {
    if (!isLabelPositionEditing) {
      setLabelOffsetDraft(null);
      setIsLabelDragging(false);
      labelDragStateRef.current = null;
    }
  }, [isLabelPositionEditing]);
  const updateLabelOffset = useCallback((nextOffset) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;
        const dataCopy = { ...(node.data || {}) };
        dataCopy.labelOffset = {
          x: Math.round(Number(nextOffset?.x) || 0),
          y: Math.round(Number(nextOffset?.y) || 0),
        };
        return { ...node, data: dataCopy };
      }),
    );
  }, [id, setNodes]);
  const handleLabelPointerDown = useCallback(
    (event) => {
      if (!isLabelPositionEditing || !labelPositionConfig) return;
      event.preventDefault();
      event.stopPropagation();
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch {}
      const currentOffset = {
        x: Number(appliedLabelOffset?.x) || 0,
        y: Number(appliedLabelOffset?.y) || 0,
      };
      labelDragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        initialX: currentOffset.x,
        initialY: currentOffset.y,
      };
      setIsLabelDragging(true);
      setLabelOffsetDraft(currentOffset);
    },
    [isLabelPositionEditing, labelPositionConfig, appliedLabelOffset],
  );
  const handleLabelPointerMove = useCallback(
    (event) => {
      if (!isLabelPositionEditing) return;
      const dragState = labelDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      const dx = event.clientX - dragState.startX;
      const dy = event.clientY - dragState.startY;
      setLabelOffsetDraft({
        x: dragState.initialX + dx,
        y: dragState.initialY + dy,
      });
    },
    [isLabelPositionEditing],
  );
  const handleLabelPointerUp = useCallback(
    (event) => {
      const dragState = labelDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      try { event.currentTarget.releasePointerCapture(event.pointerId); } catch {}
      labelDragStateRef.current = null;
      const finalOffset = labelOffsetDraft ?? {
        x: dragState.initialX,
        y: dragState.initialY,
      };
      updateLabelOffset(finalOffset);
      setLabelOffsetDraft(null);
      setIsLabelDragging(false);
    },
    [labelOffsetDraft, updateLabelOffset],
  );

  const handleHandleDoubleClick = (index) => {
    // find this bus node's position
    const self = nodes.find((n) => n.id === id);
    if (!self) return;
    const isHorizontal = angleOffset === VERTICAL_ORIENTATION;
    const axisPos = handlePositions[index] ?? index * gridGap;
    const snapCoord = (value) => Math.round(value / gridGap) * gridGap;

    // collect children connected to this specific handle
    const handleId = `handle-${index}`;
    const childIds = edges
      .filter((e) => e.source === id && e.sourceHandle === handleId)
      .map((e) => e.target);
    if (childIds.length === 0) return;

    // Build width map from React Flow measured nodes to align by center
    const rfNodes = reactFlowInstance.getNodes ? reactFlowInstance.getNodes() : [];
    const widthById = new Map();
    rfNodes.forEach((n) => {
      if (n && n.id) widthById.set(n.id, n.width || 0);
    });

    setNodes((nds) => nds.map((n) => {
      if (!childIds.includes(n.id)) return n;
      const halfW = Math.floor((widthById.get(n.id) || 0) / 2);
      if (isHorizontal) {
        const anchorX = (self.position?.x || 0) + axisPos;
        const sx = snapCoord(anchorX) - halfW;
        return { ...n, position: { ...n.position, x: sx } };
      } else {
        const anchorY = (self.position?.y || 0) + axisPos;
        const sy = snapCoord(anchorY);
        return { ...n, position: { ...n.position, y: sy } };
      }
    }));
  };

  // Map handle sides depending on rotation: when horizontal, Top->Left and Bottom->Right
  const resizerHandlePositions = horizontalBus
    ? [Position.Left, Position.Right]
    : [Position.Top, Position.Bottom];
  const targetSide = horizontalBus ? Position.Top : Position.Left;
  const sourceSide = horizontalBus ? Position.Bottom : Position.Right;
  const targetOffsetPx = horizontalBus ? -16 : 0;
  const sourceOffsetPx = horizontalBus ? 35 : 0;
  const rotatedHandleWidthPx = horizontalBus ? null : 20;
  const wrapperStyle = useMemo(() => {
    const style = {};
    if (tintColor) {
      style['--bus-tint'] = tintColor;
    }
    if (selectionAccent) {
      style['--bus-selection-accent'] = selectionAccent;
    }
    if (selectionGlow) {
      style['--bus-selection-glow'] = selectionGlow;
    }
    return style;
  }, [tintColor, selectionAccent, selectionGlow]);
  const expectedRoles = useMemo(() => (connectionAssist?.active
    ? (Array.isArray(connectionAssist.expectedRoles) && connectionAssist.expectedRoles.length > 0
      ? connectionAssist.expectedRoles
      : (connectionAssist.expectedRole ? [connectionAssist.expectedRole] : []))
    : []), [connectionAssist]);
  const hoveredNodeId = connectionAssist?.hoveredNodeId || null;
  const hoveredHandleId = connectionAssist?.hoveredHandleId || null;
  const shouldRevealHandle = useCallback(
    (role) => handlesHidden && expectedRoles.includes(role),
    [handlesHidden, expectedRoles],
  );
  const isHoveredValidHandle = useCallback(
    (handleId) => hoveredNodeId === id && hoveredHandleId === handleId,
    [hoveredHandleId, hoveredNodeId, id],
  );
  const getHandleClassName = useCallback(
    (role, handleId) => [
      'node-handle',
      `handle-node-${toHandleClassToken(id, 'node')}`,
      `handle-id-${toHandleClassToken(handleId || role, role || 'handle')}`,
      shouldRevealHandle(role) ? 'connection-assist-visible' : '',
    ]
      .filter(Boolean)
      .join(' '),
    [id, shouldRevealHandle],
  );

  useEffect(() => {
    const lengthAttr = data?.attributes?.length;
    if (!lengthAttr || lengthAttr.systemDefined) return;
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id !== id) return node;
        const nextAttributes = {
          ...(node.data?.attributes || {}),
          length: {
            ...lengthAttr,
            systemDefined: true,
          },
        };
        return {
          ...node,
          data: {
            ...(node.data || {}),
            attributes: nextAttributes,
          },
        };
      }),
    );
  }, [data?.attributes?.length, data?.attributes?.length?.systemDefined, id, setNodes]);

  return (
    <div
      className={`bus-node-wrapper ${selected ? 'bus-node-wrapper--selected' : ''}`}
      style={wrapperStyle}
      data-bus-color={tintColor || undefined}
    >
      {labelStyle ? (
        <div
          className="bus-node__labels"
          style={labelStyle}
          onPointerDown={handleLabelPointerDown}
          onPointerMove={handleLabelPointerMove}
          onPointerUp={handleLabelPointerUp}
          onPointerCancel={handleLabelPointerUp}
        >
          {visibleAttributes.map(({ key, label, value }) => (
            <div key={key} className="bus-node__label-item" data-field-key={key}>
              {!hideLabels ? (
                <span className="bus-node__label-key">
                  {typeof attributeLabelSeparator === 'string' && attributeLabelSeparator.length > 0
                    ? `${label}${attributeLabelSeparator}`
                    : label}
                </span>
              ) : null}
              <span className="bus-node__label-value">{value}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div
        ref={busRef}
        className="bus-node"
        style={{
          position: 'relative',
          width: `${busWidthPx}px`,
          height: `${busHeightPx}px`,
        }}
      >
        <NodeResizer
          isVisible={selectedNodeId == id}
          minWidth={angleOffset === VERTICAL_ORIENTATION ? minLength : thicknessPx}
          minHeight={angleOffset === VERTICAL_ORIENTATION ? thicknessPx : minLength}
          handlePositions={resizerHandlePositions}
          handleClassName="bus-node-resize-handle"
          onResizeStart={handleResizeStart}
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
        />
        {/* Target handles: top in horizontal, left in vertical. One per grid unit */}
        {handlePositions.map((axisPos, index) => {
          const handleId = `target-${index}`;
          const isHi = highlightKeys.includes(`${id}|${handleId}`);
          const isValidHover = isHoveredValidHandle(handleId);
          const style = horizontalBus
            ? {
                position: 'absolute',
                left: `${axisPos}px`,
                top: `${targetOffsetPx}px`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: isValidHover ? '#52C41A' : (isHi ? '#52C41A' : undefined),
                borderColor: isValidHover ? '#25d618' : undefined,
                zIndex: 10,
              }
            : {
                position: 'absolute',
                top: `${axisPos}px`,
                left: `-${lateralHandleOffset}px`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: isValidHover ? '#52C41A' : (isHi ? '#52C41A' : undefined),
                borderColor: isValidHover ? '#25d618' : undefined,
                zIndex: 10,
                width: rotatedHandleWidthPx ? `${rotatedHandleWidthPx}px` : undefined,
              };
          return (
            <Handle
              key={`t-${handleId}`}
              type="target"
              position={targetSide}
              id={handleId}
              className={getHandleClassName('target', handleId)}
              style={style}
            />
          );
        })}

        {/* Visual bus line fills along major axis */}
        <div className="bus-visual" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {horizontalBus ? (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: `${thicknessPx}px`,
                transform: 'translateY(-50%)',
                backgroundColor: busColor,
              }}
            />
          ) : (
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: `${thicknessPx}px`,
                transform: 'translateX(-50%)',
                backgroundColor: busColor,
              }}
            />
          )}
        </div>

        {/* Source handles: bottom in vertical, right in horizontal. Mirror target positions */}
        {handlePositions.map((axisPos, index) => {
          const handleId = `handle-${index}`;
          const isHi = highlightKeys.includes(`${id}|${handleId}`);
          const isValidHover = isHoveredValidHandle(handleId);
          const style = horizontalBus
            ? {
                position: 'absolute',
                left: `${axisPos}px`,
                top: `${sourceOffsetPx}px`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: isValidHover ? '#52C41A' : (isHi ? '#52C41A' : undefined),
                borderColor: isValidHover ? '#25d618' : undefined,
              }
            : {
                position: 'absolute',
                top: `${axisPos}px`,
                left: `${thicknessPx + lateralHandleOffset}px`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: isValidHover ? '#52C41A' : (isHi ? '#52C41A' : undefined),
                borderColor: isValidHover ? '#25d618' : undefined,
                zIndex: 10,
                width: rotatedHandleWidthPx ? `${rotatedHandleWidthPx}px` : undefined,
              };
          return (
            <Handle
              key={`s-${handleId}`}
              type="source"
              id={handleId}
              position={sourceSide}
              className={getHandleClassName('source', handleId)}
              onDoubleClick={() => handleHandleDoubleClick(index)}
              style={style}
            />
          );
        })}
      </div>
    </div>
  );
};

export default BusNode;
